import { AxiosInstance, AxiosResponse } from 'axios';
import { TCartCount } from '../types/cart';
import { TCart, TDeliverySlot } from '../types/cartDaily';
import { TLocaleAPI, TLocaleISO } from '../types/locale';

// TODO: This was used as response before,
// but the data returned does not match this @Sarith
// type TAddToCart = {
//     recommendationToken: string;
//     results: TAddToCartResult[];
// };
// type TAddToCartResult = {
//     catalogItemID: string;
// };

export type TCartDailyEndpoints = {
    getCart: (dismiss: boolean) => Promise<AxiosResponse<TCart>>;
    getCount: () => Promise<AxiosResponse<TCartCount>>;
    postToCart: (
        offerId: string,
        params: { quantity: number; attachments?: string[] },
    ) => Promise<AxiosResponse<TCartCount>>;
    postAttachments: (offerId: string, attachments: string[]) => Promise<AxiosResponse<TCart>>;
    postFbtItems: (offerCodes: string) => Promise<AxiosResponse<TCart>>;
    postSavedCart: (offerId: string) => Promise<AxiosResponse<TCart>>;
    remove: (offerId: string, cartType: string) => Promise<AxiosResponse<TCart>>;
    postMove: (isoLocale: TLocaleISO, offerId: string, cartType: string, sku: string) => Promise<AxiosResponse<TCart>>;
    put: (offerCode: string, quantity: number) => Promise<AxiosResponse<TCart>>;
    postCoupon: (code: string) => Promise<AxiosResponse<TCart>>;
    deleteCoupon: () => Promise<AxiosResponse<TCart>>;
    postCity: (cityId: number, paramString?: string) => Promise<AxiosResponse<TCart>>;
    postFreebie: (offerCode: string) => Promise<AxiosResponse<TCart>>;
    postExperience: (experience: string) => Promise<AxiosResponse<TCart>>;
    postShippingPreference: (shippingPreference: { [code: string]: boolean }) => Promise<AxiosResponse<TCart>>;
    postPreference: (preference: string, value: string) => Promise<AxiosResponse<TCart>>;
    getDeliverySlots: (groupIx: number) => Promise<AxiosResponse<{ error?: string; deliverySlots: TDeliverySlot[] }>>;
    postDeliverySlotChange: (
        groupIx: number,
        data: { deliveryTimeSlotCode: string; deliveryDate: string },
    ) => Promise<AxiosResponse<TCart>>;
    reorderOrder: (orderNumber: string) => Promise<AxiosResponse<TCart>>;
    postPaymentCardSubscription: (
        paymentCardId: string,
        cvv: string,
    ) => Promise<AxiosResponse<{ reference: string; status: string }>>;
    getPaymentCardSubscriptionStatus: (
        refNumber: string,
    ) => Promise<AxiosResponse<{ reference: string; status: string }>>;
    postOrder: (paymentId: number, requestConfig?) => Promise<AxiosResponse<TCart>>;
    getPlaceOrderStatus: (orderNumber: string, cvv?: any) => Promise<AxiosResponse<TCart>>;
    getProcessingOrderStatus: (orderNumber: string) => Promise<AxiosResponse<any>>;
};

export default (api: AxiosInstance, apiLocale: TLocaleAPI): TCartDailyEndpoints => {
    // TODO: Why was dismiss removed before? @Sarith
    const getCart = async (dismiss: boolean): Promise<AxiosResponse<TCart>> =>
        api.get(`/_svc/cart-v1/cart?showCart&isPageCart${dismiss ? '&dismiss=true' : ''}`, {
            headers: { 'X-Locale': apiLocale },
        });

    const getCount = async (): Promise<AxiosResponse<TCartCount>> =>
        api.get<TCartCount>('/_svc/cart-v1/cart/count', { headers: { 'X-Locale': apiLocale } });

    const postToCart = async (
        offerId: string,
        params: { quantity: number; attachments?: string[] } = { quantity: 1 },
    ): Promise<AxiosResponse<TCartCount>> => {
        return api.post<TCartCount>(
            `/_svc/cart-v1/cart/item/${offerId}/${params.quantity}?showCart`,
            {
                offerId,
                ...params,
            },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const postAttachments = async (offerId: string, attachments: string[]): Promise<AxiosResponse<TCart>> => {
        return api.post(
            `/_svc/cart-v1/cart/attach/${offerId}?showCart`,
            {
                attachments,
            },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const postFbtItems = async (offerCodes: string): Promise<AxiosResponse<TCart>> => {
        return api.post<TCart>(
            `/_svc/cart-v1/cart/items/${offerCodes}?showCart`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );
    };

    const postSavedCart = async (offerId: string): Promise<AxiosResponse<TCart>> => {
        return api.post(
            `/_svc/cart-v1/savedcart/item/${offerId}?showCart`,
            {
                offerId,
            },
            { headers: { 'X-Locale': apiLocale } },
        );
    };

    const remove = async (offerId: string, cartType: string): Promise<AxiosResponse<TCart>> => {
        return api.delete<TCart>(`/_svc/cart-v1/${cartType}/item/${offerId}?showCart`, {
            headers: { 'X-Locale': apiLocale },
        });
    };

    const postMove = async (
        isoLocale: TLocaleISO,
        offerId: string,
        cartType: string,
        sku: string,
    ): Promise<AxiosResponse<TCart>> => {
        switch (cartType) {
            case 'savedcart':
                return api.post<TCart>(
                    `/_svc/cart-v1/cart/move/${offerId}/savedcart?showCart`,
                    {},
                    {
                        headers: { 'X-Locale': apiLocale },
                    },
                );
            case 'cart':
            default:
                return api.post<TCart>(
                    `/_svc/cart-v1/savedcart/move/${offerId}/cart?showCart`,
                    {},
                    {
                        headers: { 'X-Locale': apiLocale },
                    },
                );
        }
    };

    const put = async (offerCode: string, quantity: number): Promise<AxiosResponse<TCart>> =>
        api.put<TCart>(
            `/_svc/cart-v1/cart/item/${offerCode}/${quantity}?showCart`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const postCoupon = async (code: string): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(`/_svc/cart-v1/coupon?code=${code}&showCart`, {}, { headers: { 'X-Locale': apiLocale } });

    const deleteCoupon = async (): Promise<AxiosResponse<TCart>> =>
        api.delete<TCart>(`/_svc/cart-v1/coupon?&showCart`, { headers: { 'X-Locale': apiLocale } });

    const postCity = (cityId: number, paramString = ''): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(
            `/_svc/cart-v1/destination?id_city=${cityId}${paramString}`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const postFreebie = (offerCode: string): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>( // Check type again
            `/_svc/cart-v1/cart/freebie/${offerCode}`, {}, { headers: { 'X-Locale': apiLocale } });

    // DAILY SPECIFICS
    const postExperience = (experience: string): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>( // Check type again
            `/_svc/cart-v1/switch-experience/${experience}?showCart&isPageCart`,
            {},
            { headers: { 'X-Locale': apiLocale } },
        );
    // setDailyCheckoutExperience(experience: string, isPageCart?: boolean) {
    //     const uri = new URI(`/_svc/cart-v1/switch-experience/${experience}`);
    //     const uriWithQuery = uri.search({ isPageCart, showCart: true }).toString();
    //     return api.post(uriWithQuery, {}, { headers: { 'X-Locale': apiLocale } });
    //   },

    const postShippingPreference = (shippingPreference: { [x: string]: boolean }): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(
            '/_svc/cart-v1/cart/shipping-preference?showCart',
            { shippingPreferences: shippingPreference },
            { headers: { 'X-Locale': apiLocale } },
        );

    const postPreference = (preference: string, value: string): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(
            `/_svc/cart-v1/cart/preference/${preference}/${value}?showCart`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const getDeliverySlots = (
        groupIx: number,
    ): Promise<AxiosResponse<{ error?: string; deliverySlots: TDeliverySlot[] }>> =>
        api.get<{ error?: string; deliverySlots: TDeliverySlot[] }>(`/_svc/cart-v1/delivery-slots/${groupIx}`, {
            headers: { 'X-Locale': apiLocale },
        });

    const postDeliverySlotChange = (
        groupIx: number,
        data: { deliveryTimeSlotCode: string; deliveryDate: string },
    ): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(`/_svc/cart-v1/delivery-slots/${groupIx}?showCart`, data, {
            headers: { 'X-Locale': apiLocale },
        });

    const reorderOrder = (orderNumber: string): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(
            `/_svc/cart-v1/cart/reorder/${orderNumber}`,
            {},
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const postPaymentCardSubscription = (
        paymentCardId: string,
        cvv: string,
    ): Promise<AxiosResponse<{ reference: string; status: string }>> =>
        api.post<{ reference: string; status: string }>(
            `/_svc/customer-v1/customer/paymentcards/subscription/${paymentCardId}`,
            { cvv },
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const getPaymentCardSubscriptionStatus = (
        refNumber: string,
    ): Promise<AxiosResponse<{ reference: string; status: string }>> =>
        api.get<{ reference: string; status: string }>(
            `/_svc/customer-v1/customer/paymentcards/subscription/${refNumber}`,
            {
                headers: { 'X-Locale': apiLocale },
            },
        );

    const postOrder = (paymentId: number, cvv?: any): Promise<AxiosResponse<TCart>> =>
        api.post<TCart>(
            '/_svc/checkout/v1/checkout',
            { paymentId },
            {
                headers: {
                    'X-Locale': apiLocale,
                    ...cvv,
                },
            },
        );

    const getPlaceOrderStatus = (orderNumber: string, cvv?: any): Promise<AxiosResponse<any>> =>
        api.get<any>(`/_svc/checkout/v1/checkout/${orderNumber}`, {
            headers: {
                'X-Locale': apiLocale,
                ...cvv,
            },
        });

    // return api.get(`/_svc/checkout/v1/checkout/${orderNumber}`, requestConfig);

    const getProcessingOrderStatus = (orderNumber: string): Promise<AxiosResponse<any>> =>
        api.get<any>(`/_svc/checkout/v2/checkout/${orderNumber}`, {
            headers: { 'X-Locale': apiLocale },
        });

    return {
        getCart,
        getCount,
        postToCart,
        postAttachments,
        postFbtItems,
        postSavedCart,
        remove,
        postMove,
        put,
        postCoupon,
        deleteCoupon,
        postCity,
        postFreebie,
        postExperience,
        postShippingPreference,
        postPreference,
        getDeliverySlots,
        postDeliverySlotChange,
        reorderOrder,
        postPaymentCardSubscription,
        getPaymentCardSubscriptionStatus,
        postOrder,
        getPlaceOrderStatus,
        getProcessingOrderStatus,
    };
};
